import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@1trading/react/components/ui/form';
import { PopoverContent, PopoverControlled, PopoverTrigger } from '@1trading/react/components/ui/popover';
import { PseudoInputButton, PseudoInputIcon, PseudoInputLabel, PseudoInputOptions } from '@1trading/react/components/ui/pseudo-input';
import { ChevronsUpDown } from 'lucide-react';
import { type FieldPathByValue, type FieldValues, type Path, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useMainstreamCountries } from '@/hooks/queries/use-mainstream-countries';

type InstitutionFieldCountryProps<T extends FieldValues> = {
  label: string;
  name: FieldPathByValue<T, string>;
  placeholder: string;
};

const InstitutionFieldCountry = <T extends FieldValues>({ label, name, placeholder }: InstitutionFieldCountryProps<T>) => {
  const form = useFormContext<T>();
  const { countriesAsOptions } = useMainstreamCountries();
  const { t } = useTranslation();

  return (
    <FormField
      control={form.control}
      name={name as Path<T>}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}*</FormLabel>
          <PopoverControlled>
            {(_, setOpen) => (
              <>
                <PopoverTrigger asChild>
                  <FormControl>
                    <PseudoInputButton role="combobox" value={field.value} {...(!field.value && { 'data-placeholder': '' })}>
                      <PseudoInputLabel>{field.value ? countriesAsOptions.find((option) => option.value === field.value)?.label : placeholder}</PseudoInputLabel>
                      <PseudoInputIcon>
                        <ChevronsUpDown />
                      </PseudoInputIcon>
                    </PseudoInputButton>
                  </FormControl>
                </PopoverTrigger>
                <PopoverContent className="p-0" stretch>
                  <PseudoInputOptions
                    empty={t('statuses.noResults')}
                    options={countriesAsOptions}
                    placeholder={t('fundamentals.searchCountries')}
                    value={field.value}
                    onValueChange={(value) => {
                      field.onChange(field.value === value ? '' : value);
                      setOpen(false);
                    }}
                  />
                </PopoverContent>
              </>
            )}
          </PopoverControlled>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};
InstitutionFieldCountry.displayName = 'InstitutionFieldCountry';

export { InstitutionFieldCountry };
